import { X } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { COLLECTION_PERMISSIONS, type CollectionPermission } from "~/api/libraryTypes";
import {
  useArchiveCollection,
  useCollectionGroups,
  useCollectionPeople,
  useGrantCollectionAccess,
  useGrantCollectionGroupAccess,
  useGroups,
  useMembers,
  useRestoreCollection,
  useRevokeCollectionAccess,
  useRevokeCollectionGroupAccess,
} from "~/api/queries";
import { ABILITY, type Collection } from "~/api/types";
import { Select } from "~/features/settings/controls";
import { Button } from "~/foundation/components/Button";
import { Dialog } from "~/foundation/components/Dialog";
import { IconButton } from "~/foundation/components/IconButton";
import { Spinner } from "~/foundation/components/Spinner";
import { Text } from "~/foundation/components/Text";

const PERMISSION_LABELS: Record<CollectionPermission, (t: (key: string) => string) => string> = {
  read: (t) => t("Can read"),
  read_write: (t) => t("Can edit"),
  admin: (t) => t("Can manage"),
};

/**
 * W10 §8 — who can see a collection, and the collection's own lifecycle.
 *
 * Four things this surface takes from the controller rather than from a UI convention:
 *
 * 1. **Permission is asymmetric on the wire** — `read_write` out, `readwrite` back. The adapter
 *    normalises, so this file only ever sees the underscored form.
 * 2. **`add_group` returns nothing.** There is no membership row to patch in; the refetch is
 *    the update, which is why the group half has no optimistic state.
 * 3. **Archive is gated on DELETE**, not on update: archiving hides every document inside the
 *    collection, so the platform treats it as destructive, and so does this.
 * 4. **A membership list carries no `query` search.** The request record has the field and the
 *    handler never reads it, so filtering here would be client-side make-believe; the lists are
 *    short and shown whole instead.
 */
export function CollectionAccessDialog({
  collection,
  onClose,
}: {
  collection: Collection;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const people = useCollectionPeople(collection.id);
  const groups = useCollectionGroups(collection.id);
  const { data: members } = useMembers();
  const { data: allGroups } = useGroups();
  const grant = useGrantCollectionAccess();
  const revoke = useRevokeCollectionAccess();
  const grantGroup = useGrantCollectionGroupAccess();
  const revokeGroup = useRevokeCollectionGroupAccess();
  const archive = useArchiveCollection();
  const restore = useRestoreCollection();

  const [userId, setUserId] = useState("");
  const [userPermission, setUserPermission] = useState<CollectionPermission>("read_write");
  const [groupId, setGroupId] = useState("");
  const [groupPermission, setGroupPermission] = useState<CollectionPermission>("read_write");

  const canManage = collection.abilities[ABILITY.collectionUpdate] === true;
  const canArchive = collection.abilities[ABILITY.collectionDelete] === true;
  const memberName = (id: string) => members?.find((member) => member.id === id)?.name ?? id;
  const groupName = (id: string) => allGroups?.find((group) => group.id === id)?.name ?? id;

  const granted = new Set((people.data?.memberships ?? []).map((row) => row.userId));
  const groupGranted = new Set((groups.data?.memberships ?? []).map((row) => row.groupId));

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("Access to “{{name}}”", { name: collection.name })}
      closeLabel={t("Close")}
      size="md"
      footer={
        <Button variant="ghost" onClick={onClose}>
          {t("Done")}
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <section aria-label={t("People")} className="flex flex-col gap-2">
          <Text variant="small" className="uppercase tracking-wide">
            {t("People")}
          </Text>

          {people.isLoading ? (
            <Spinner label={t("Loading")} />
          ) : people.data?.memberships.length ? (
            <ul className="flex flex-col rounded-md border border-divider">
              {people.data.memberships.map((membership) => (
                <li
                  key={membership.id}
                  className="flex items-center gap-2 border-b border-divider px-3 py-2 last:border-b-0"
                >
                  <span className="min-w-0 flex-1 truncate text-body text-text">
                    {memberName(membership.userId)}
                  </span>
                  <Select
                    aria-label={t("Permission for {{name}}", {
                      name: memberName(membership.userId),
                    })}
                    value={membership.permission}
                    disabled={!canManage || grant.isPending}
                    onChange={(event) =>
                      grant.mutate({
                        collectionId: collection.id,
                        userId: membership.userId,
                        permission: event.target.value as CollectionPermission,
                      })
                    }
                  >
                    {COLLECTION_PERMISSIONS.map((permission) => (
                      <option key={permission} value={permission}>
                        {PERMISSION_LABELS[permission](t)}
                      </option>
                    ))}
                  </Select>
                  {canManage ? (
                    <IconButton
                      icon={X}
                      size="sm"
                      aria-label={t("Remove {{name}}", { name: memberName(membership.userId) })}
                      onClick={() =>
                        revoke.mutate({ collectionId: collection.id, userId: membership.userId })
                      }
                    />
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <Text variant="secondary">
              {t("Nobody has been given access individually — workspace rules apply.")}
            </Text>
          )}

          {canManage ? (
            <div className="flex items-end gap-2">
              <Select
                label={t("Add a person")}
                value={userId}
                onChange={(event) => setUserId(event.target.value)}
              >
                <option value="">{t("Choose someone")}</option>
                {(members ?? [])
                  .filter((member) => !granted.has(member.id))
                  .map((member) => (
                    <option key={member.id} value={member.id}>
                      {member.name}
                    </option>
                  ))}
              </Select>
              <Select
                label={t("Permission")}
                value={userPermission}
                onChange={(event) => setUserPermission(event.target.value as CollectionPermission)}
              >
                {COLLECTION_PERMISSIONS.map((permission) => (
                  <option key={permission} value={permission}>
                    {PERMISSION_LABELS[permission](t)}
                  </option>
                ))}
              </Select>
              <Button
                variant="neutral"
                disabled={!userId || grant.isPending}
                onClick={() =>
                  grant.mutate(
                    { collectionId: collection.id, userId, permission: userPermission },
                    { onSuccess: () => setUserId("") },
                  )
                }
              >
                {t("Add")}
              </Button>
            </div>
          ) : null}
        </section>

        <section
          aria-label={t("Groups")}
          className="flex flex-col gap-2 border-t border-divider pt-3"
        >
          <Text variant="small" className="uppercase tracking-wide">
            {t("Groups")}
          </Text>

          {groups.data?.memberships.length ? (
            <ul className="flex flex-col rounded-md border border-divider">
              {groups.data.memberships.map((membership) => (
                <li
                  key={membership.id}
                  className="flex items-center gap-2 border-b border-divider px-3 py-2 last:border-b-0"
                >
                  <span className="min-w-0 flex-1 truncate text-body text-text">
                    {groupName(membership.groupId)}
                  </span>
                  <Select
                    aria-label={t("Permission for {{name}}", {
                      name: groupName(membership.groupId),
                    })}
                    value={membership.permission}
                    disabled={!canManage || grantGroup.isPending}
                    onChange={(event) =>
                      grantGroup.mutate({
                        collectionId: collection.id,
                        groupId: membership.groupId,
                        permission: event.target.value as CollectionPermission,
                      })
                    }
                  >
                    {COLLECTION_PERMISSIONS.map((permission) => (
                      <option key={permission} value={permission}>
                        {PERMISSION_LABELS[permission](t)}
                      </option>
                    ))}
                  </Select>
                  {canManage ? (
                    <IconButton
                      icon={X}
                      size="sm"
                      aria-label={t("Remove {{name}}", { name: groupName(membership.groupId) })}
                      onClick={() =>
                        revokeGroup.mutate({
                          collectionId: collection.id,
                          groupId: membership.groupId,
                        })
                      }
                    />
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <Text variant="secondary">{t("No group has access to this collection yet.")}</Text>
          )}

          {canManage ? (
            <div className="flex items-end gap-2">
              <Select
                label={t("Add a group")}
                value={groupId}
                onChange={(event) => setGroupId(event.target.value)}
              >
                <option value="">{t("Choose a group")}</option>
                {(allGroups ?? [])
                  .filter((group) => !groupGranted.has(group.id))
                  .map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
              </Select>
              <Select
                label={t("Permission")}
                value={groupPermission}
                onChange={(event) => setGroupPermission(event.target.value as CollectionPermission)}
              >
                {COLLECTION_PERMISSIONS.map((permission) => (
                  <option key={permission} value={permission}>
                    {PERMISSION_LABELS[permission](t)}
                  </option>
                ))}
              </Select>
              <Button
                variant="neutral"
                disabled={!groupId || grantGroup.isPending}
                onClick={() =>
                  grantGroup.mutate(
                    { collectionId: collection.id, groupId, permission: groupPermission },
                    { onSuccess: () => setGroupId("") },
                  )
                }
              >
                {t("Add")}
              </Button>
            </div>
          ) : null}
        </section>

        {canArchive ? (
          <section
            aria-label={t("Lifecycle")}
            className="flex items-center justify-between gap-3 border-t border-divider pt-3"
          >
            <div className="min-w-0">
              <Text variant="small" className="font-medium text-text">
                {collection.archivedAt ? t("Archived") : t("Active")}
              </Text>
              <Text variant="small" className="text-text-tertiary">
                {t("Archiving hides the collection and every document inside it.")}
              </Text>
            </div>
            <Button
              variant={collection.archivedAt ? "neutral" : "danger"}
              disabled={archive.isPending || restore.isPending}
              onClick={() =>
                collection.archivedAt
                  ? restore.mutate(collection.id, {
                      onSuccess: () => toast.success(t("Collection restored")),
                    })
                  : archive.mutate(collection.id, {
                      onSuccess: () => {
                        toast.success(t("Collection archived"));
                        onClose();
                      },
                    })
              }
            >
              {collection.archivedAt ? t("Restore") : t("Archive")}
            </Button>
          </section>
        ) : null}
      </div>
    </Dialog>
  );
}
